/**
 * Success page after publishing a Pakun tööd job.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, useLanguage } from '@/lib/context';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GlobalNav } from '@/components/GlobalNav';
import { CheckCircle2, ExternalLink, Users, Copy, Share2, Edit3, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRY_LABELS = {
  EE: 'Eesti', FI: 'Soome', NO: 'Norra', SE: 'Rootsi', DK: 'Taani',
  DE: 'Saksamaa', PL: 'Poola', LV: 'Läti', LT: 'Leedu', NL: 'Holland', GB: 'Suurbritannia',
};

export default function CreateJobSuccessPage() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const jobId = params.get('jobId');

  useEffect(() => {
    if (!jobId) { navigate('/jobs/create', { replace: true }); return; }
    (async () => {
      try {
        const res = await api.get(`/jobs/${jobId}`);
        setJob(res.data);
      } catch (_) {
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  const publicUrl = job ? `${window.location.origin}/jobs/${job.id}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t('jobWizard.success.copied'));
    } catch (_) {
      toast.error(t('common.error'));
    }
  };

  const shareFb = () => {
    const url = encodeURIComponent(publicUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <GlobalNav />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 grid place-items-center mb-3">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900" data-testid="success-heading">
                {t('jobWizard.success.title')}
              </h1>
              <p className="text-slate-600 mt-2">
                {t('jobWizard.success.body')}
              </p>
            </div>

            <div className="mt-8 rounded-xl border border-slate-200 p-5 bg-slate-50/60">
              <div className="text-xl font-bold text-slate-900" data-testid="success-job-title">
                {job.title}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {[job.city, COUNTRY_LABELS[job.country] || job.country].filter(Boolean).join(', ')}
              </div>
              <div className="text-xs text-slate-500 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                <span>{t('jobWizard.success.publisher')}: <b className="text-slate-800">{job.company_name}</b></span>
                <span>{t('jobWizard.success.publishedOn')}: <b className="text-slate-800">{(job.created_at || '').slice(0, 10)}</b></span>
                {job.expires_at && <span>{t('jobWizard.success.expiresOn')}: <b className="text-slate-800">{(job.expires_at || '').slice(0, 10)}</b></span>}
                <span className="text-emerald-700">● {t('jobWizard.success.active')}</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <Button onClick={() => navigate(`/jobs/${job.id}`)} data-testid="success-view">
                <ExternalLink className="h-4 w-4 mr-2" /> {t('jobWizard.success.view')}
              </Button>
              <Button variant="secondary" onClick={() => navigate(`/company/jobs/${job.id}/applications`)}
                      data-testid="success-manage">
                <Users className="h-4 w-4 mr-2" /> {t('jobWizard.success.manage')}
              </Button>
              <Button variant="outline" onClick={shareFb} data-testid="success-share-fb">
                <Share2 className="h-4 w-4 mr-2" /> {t('jobWizard.success.shareFb')}
              </Button>
              <Button variant="outline" onClick={copyLink} data-testid="success-copy">
                <Copy className="h-4 w-4 mr-2" /> {t('jobWizard.success.copyLink')}
              </Button>
              <Button variant="ghost" onClick={() => navigate(`/company/jobs`)} data-testid="success-edit">
                <Edit3 className="h-4 w-4 mr-2" /> {t('jobWizard.success.edit')}
              </Button>
              <Link to="/jobs/create">
                <Button variant="ghost" className="w-full" data-testid="success-new">
                  <Plus className="h-4 w-4 mr-2" /> {t('jobWizard.success.new')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
